<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { useClientAccountStore } from '@/stores/clientAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

definePage({
  meta: {
    layout: 'blank',
    public: true,
    clientArea: true,
  },
})

const accountStore = useClientAccountStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const token = computed(() => String(route.query.token || ''))
const formError = ref('')
const isPasswordVisible = ref(false)
const refVForm = ref()

const form = ref({
  password: '',
  password_confirmation: '',
  name: '',
  phone: '',
  address_line1: '',
  city: '',
  postal_code: '',
  country: '',
})

onMounted(async () => {
  if (!token.value) {
    formError.value = t('clientAcceptInvite.missingToken')

    return
  }
  try {
    await accountStore.fetchInvite(token.value)
    if (accountStore.invitePreview?.client_name)
      form.value.name = accountStore.invitePreview.client_name
  }
  catch {
    // invitePreview holds the error message
  }
})

const submit = async () => {
  formError.value = ''
  try {
    await accountStore.acceptInvite(token.value, {
      password: form.value.password,
      password_confirmation: form.value.password_confirmation,
      name: form.value.name,
      phone: form.value.phone,
      address_line1: form.value.address_line1,
      city: form.value.city,
      postal_code: form.value.postal_code,
      country: form.value.country,
    })
    await router.replace('/client/today')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      submit()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="560"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <div class="d-flex align-center gap-x-3 mb-6">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">FitX</span>
        </div>

        <h1 class="text-h4 mb-1">
          {{ $t('clientAcceptInvite.title') }}
        </h1>
        <p class="text-medium-emphasis">
          {{ accountStore.invitePreview?.message || $t('clientAcceptInvite.loadingInvitation') }}
        </p>
        <p
          v-if="accountStore.invitePreview?.trainer_business_name"
          class="text-body-2"
        >
          {{ $t('clientAcceptInvite.invitedBy') }} <strong>{{ accountStore.invitePreview.trainer_business_name }}</strong>
        </p>
      </VCardText>

      <VCardText v-if="accountStore.invitePreview?.valid">
        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                :model-value="accountStore.invitePreview.email"
                :label="$t('common.email')"
                disabled
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.password"
                :label="$t('common.password')"
                :type="isPasswordVisible ? 'text' : 'password'"
                :rules="[requiredValidator]"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="form.password_confirmation"
                :label="$t('common.confirmPassword')"
                type="password"
                :rules="[requiredValidator]"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                :label="$t('common.name')"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.phone"
                :label="$t('common.phone')"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.address_line1"
                :label="$t('clientAcceptInvite.addressLabel')"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="form.city"
                :label="$t('common.city')"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="form.postal_code"
                :label="$t('common.postalCode')"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="form.country"
                :label="$t('common.country')"
              />
            </VCol>
            <VCol cols="12">
              <VBtn
                block
                type="submit"
                color="primary"
                :loading="accountStore.loading"
              >
                {{ $t('clientAcceptInvite.submit') }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VCardText v-else-if="!accountStore.loading">
        <VAlert
          type="error"
          variant="tonal"
          role="alert"
        >
          {{ accountStore.invitePreview?.message || formError || $t('clientAcceptInvite.invalidInvite') }}
        </VAlert>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
